import { Emitter } from "@core/events/emitter";
import type { IDebugModeModel } from "./IDebugModeModel";

/**
 * `DebugModeModel` — the persisted implementation of {@link IDebugModeModel}.
 *
 * Headless and dependency-free: the flag, its `localStorage` mirror, and a
 * change emitter. See the interface for what the flag gates and why it is an
 * IOC model.
 *
 * The key follows the `Campfire.Dev.*` convention the scene-statistics panel
 * already uses for its open state (`Campfire.Dev.SceneStatistics`) — a
 * developer preference, not a cache entry, so `AppCacheModel`'s "Clear Cache"
 * deliberately leaves it alone alongside `Campfire.AI.*` and
 * `Campfire.Workflow.DevMode`.
 */
const K_PREF_KEY = "Campfire.Dev.DebugMode";

export class DebugModeModel implements IDebugModeModel {
  readonly onChanged = new Emitter<boolean>();

  private m_isEnabled = readPref();

  get isEnabled(): boolean {
    return this.m_isEnabled;
  }

  setEnabled(enabled: boolean): void {
    if (this.m_isEnabled === enabled) return;
    this.m_isEnabled = enabled;
    writePref(enabled);
    this.onChanged.emit(enabled);
  }

  toggle(): boolean {
    this.setEnabled(!this.m_isEnabled);
    return this.m_isEnabled;
  }
}

/**
 * Absence reads as OFF, so a first run — and any browser refusing storage —
 * lands on the safe default rather than exposing developer surfaces.
 */
function readPref(): boolean {
  try {
    return window.localStorage.getItem(K_PREF_KEY) === "1";
  } catch {
    return false;
  }
}

function writePref(enabled: boolean): void {
  try {
    if (enabled) window.localStorage.setItem(K_PREF_KEY, "1");
    else window.localStorage.removeItem(K_PREF_KEY);
  } catch {
    // Private-mode storage refusal: the flag stays session-only, which is a
    // better outcome than failing the toggle the user just asked for.
  }
}
